import React, { useState, useEffect, useRef } from 'react';
import './ClassSelection.css';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setClasses, setClassesLoading, setClassesError, setSelectedClass as setSelectedClassRedux, clearSelectedClass } from '../../../redux/slices/classSlice';
import classService from '../services/classService';
import Login from '../../../components/Login';

const ClassSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const categoryId = searchParams.get('categoryId');
  const fetchingRef = useRef(false);

  // Redux state
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { classes: classesCache, classesLoading, classesError, selectedClass: selectedClassRedux } = useSelector((state) => state.class);
  
  // Local state - initialize from Redux to preserve selection when navigating back
  const [selectedClass, setSelectedClass] = useState(selectedClassRedux);
  const [showLogin, setShowLogin] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Get classes from cache
  const cacheKey = `${centerId}_${categoryId}`;
  const classes = classesCache[cacheKey] || [];
  const loading = classesLoading;
  const error = classesError;

  // Auto-open login if returning from registration
  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLogin(true);
    }
  }, []);

  useEffect(() => {
    if (!centerId || !categoryId) {
      return;
    }

    // Check if data is already cached before making API call
    const cachedClasses = classesCache[cacheKey];
    if (cachedClasses && cachedClasses.length > 0) {
      return; // Data already loaded, skip API call
    }

    // Check if already fetching
    if (fetchingRef.current) {
      return;
    }

    fetchClasses();
  }, [centerId, categoryId]);

  const fetchClasses = async () => {
    fetchingRef.current = true;
    try {
      dispatch(setClassesLoading(true));
      const data = await classService.getClassesByCategory(categoryId, centerId);
      dispatch(setClasses({ centerId, categoryId, classes: data || [] }));
      dispatch(setClassesError(null));
    } catch (err) {
      dispatch(setClassesError('Failed to load classes. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setClassesLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectClass = async (classItem) => {
    console.log('Selected class data:', classItem);
    
    // Fetch full class details from API
    try {
      setDetailsLoading(true);
      const fullDetails = await classService.getClassDetails(classItem.class_Id);
      // Preserve instructor_Id from the list item in case the detail API omits it
      if (!fullDetails.instructor_Id && !fullDetails.Instructor_Id) {
        fullDetails.instructor_Id = classItem.instructor_Id || classItem.Instructor_Id || 0;
      }
      setSelectedClass(fullDetails);
      // Save to Redux for persistence
      dispatch(setSelectedClassRedux(fullDetails));
    } catch (error) {
      console.error('Error fetching class details:', error);
      // Fallback to basic class data if API fails
      setSelectedClass(classItem);
      dispatch(setSelectedClassRedux(classItem));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleProceedToRegistration = () => {
    if (selectedClass) {
      if (isAuthenticated) {
        navigate(`/class/enroll-student?classId=${selectedClass.class_Id}`);
      } else {
        setShowLogin(true);
      }
    }
  };

  const handleLoginSuccess = (userData) => {
    setShowLogin(false);
    if (selectedClass) {
      navigate(`/class/enroll-student?classId=${selectedClass.class_Id}`);
    }
  };

  const handleLoginCancel = () => {
    setShowLogin(false);
  };

  const handleScheduleClick = async () => {
    if (!selectedClass) return;
    
    // Toggle visibility if sessions already loaded
    if (sessions.length > 0) {
      setShowSessions(!showSessions);
      return;
    }
    
    // Fetch sessions if not already loaded
    try {
      setSessionsLoading(true);
      const sessionData = await classService.getClassSessions(selectedClass.class_Id);
      console.log('Session data received:', sessionData);
      console.log('First session:', sessionData?.[0]);
      setSessions(sessionData || []);
      setShowSessions(true);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      alert('Failed to load class sessions. Please try again.');
    } finally {
      setSessionsLoading(false);
    }
  };

  return (
    <div className="class-selection-page min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8">
      <div className="container-custom">
        {/* Header - Aligned with Back Button */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                Choose Your Class
              </h1>
              <p className="text-sm text-gray-500">
                Select a class to view details and register
              </p>
            </div>
            <button
              onClick={() => {
                dispatch(clearSelectedClass());
                setSelectedClass(null);
                navigate(`/class/category?centerId=${centerId}`);
              }}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading classes...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Classes Grid - Tile Layout */}
        {!loading && classes.length > 0 && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
              {classes.map((classItem, index) => (
              <div
                key={classItem.class_Id}
                onClick={() => handleSelectClass(classItem)}
                className={`item-card cursor-pointer ${
                  selectedClass?.class_Id === classItem.class_Id
                    ? 'selected'
                    : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Selection Tick Mark */}
                {selectedClass?.class_Id === classItem.class_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg className="w-5 h-5 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                
                {/* Animated gradient border effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-700 -z-10"></div>
                
                {/* Shine effect on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </div>

                {/* Image Section with Fixed Aspect Ratio */}
                <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
                  {classItem.class_Image_Url ? (
                    <>
                      <img
                        src={classItem.class_Image_Url?.trim()}
                        alt={classItem.class_Name}
                        className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-110 group-hover:rotate-2 transition-all duration-700 ease-out filter group-hover:brightness-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden absolute inset-0 w-full h-full items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 animate-gradient-xy">
                        <span className="text-white text-7xl drop-shadow-2xl animate-bounce">📚</span>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 animate-gradient-xy">
                      <span className="text-white text-7xl drop-shadow-2xl animate-bounce">📚</span>
                    </div>
                  )}
                  
                  {/* Animated gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/30 via-purple-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
                
                {/* Content Section */}
                <div className="p-6 bg-gradient-to-b from-white to-gray-50/50">
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-3 leading-snug">
                    {classItem.class_Name}
                  </h3>

                  {/* Teacher */}
                  {classItem.instructorName && (
                    <div className="mb-5 text-base text-gray-600 font-semibold">
                      👨‍🏫 {classItem.instructorName}
                    </div>
                  )}

                  <button className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-5 rounded-full border border-gray-200 transition shadow-sm hover:shadow-md text-base">
                    View Details
                  </button>
                </div>
              </div>
              ))}
            </div>

            {/* Side Drawer Overlay */}
            {selectedClass && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 bg-black/50 z-40 animate-fadeIn"
                  onClick={() => setSelectedClass(null)}
                ></div>
                
                {/* Side Drawer - Compact */}
                <div className="fixed top-0 right-0 h-full w-full md:w-[480px] bg-gradient-to-br from-yellow-50 via-white to-yellow-50 shadow-2xl z-50 overflow-y-auto animate-slideInRight">
                  {/* Header - Sleek Design */}
                  <div className="sticky top-0 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-700 pr-4 line-clamp-2">{selectedClass.class_Name}</h2>
                    <button onClick={() => setSelectedClass(null)} className="text-gray-400 hover:text-gray-600 hover:rotate-90 text-xl flex-shrink-0 transition-all duration-300">✕</button>
                  </div>
                  
                  {/* Content - Compact */}
                  <div className="p-4">
                    {/* Register Button and Schedule */}
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <button 
                        onClick={handleProceedToRegistration}
                        className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-5 rounded-full border-2 border-gray-200 transition-all duration-200 shadow-md hover:shadow-lg text-sm flex items-center gap-2"
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06), inset 0 -2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        ⚡ Register
                      </button>
                      <button 
                        onClick={handleScheduleClick}
                        disabled={sessionsLoading}
                        className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-5 rounded-full border-2 border-gray-200 transition-all duration-200 shadow-md hover:shadow-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06), inset 0 -2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        {sessionsLoading ? '⏳' : '📆'} Schedule
                      </button>
                    </div>
                    
                    {/* Image - Compact */}
                    {selectedClass.class_Image_Url && (
                      <div className="mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-white to-blue-50 shadow-md p-4 border border-blue-100">
                        <img src={selectedClass.class_Image_Url?.trim()} alt={selectedClass.class_Name} className="w-full h-32 object-contain" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Class'; }} />
                      </div>
                    )}
                    
                    {/* Details Grid - Compact */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* Teacher */}
                      {selectedClass.instructorName && (
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">👨‍🏫 Teacher</p>
                          {(() => {
                            const instId = selectedClass.instructor_Id || selectedClass.Instructor_Id;
                            const instName = selectedClass.instructorName || '';
                            const dest = instId && instId !== 0
                              ? `/class/instructor/${instId}`
                              : `/class/instructor/0`;
                            return (
                              <div className="flex flex-col gap-0.5">
                                <button
                                  onClick={() => navigate(dest, { state: { instructorName: instName, instructorId: instId } })}
                                  title="View instructor profile"
                                  className="flex items-center gap-1.5 font-bold text-indigo-700 hover:text-indigo-900 text-sm text-left transition-colors group"
                                >
                                  <span>{instName}</span>
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 flex-shrink-0 group-hover:scale-110" style={{ background: 'linear-gradient(135deg,#f97316,#fbbf24)', boxShadow: '0 2px 6px rgba(249,115,22,0.35)' }}>
                                    <svg className="w-3.5 h-3.5" style={{ color: '#ffffff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </span>
                                </button>
                                <button
                                  onClick={() => navigate(dest, { state: { instructorName: instName, instructorId: instId } })}
                                  className="text-xs font-semibold text-left flex items-center gap-1 transition-colors hover:underline"
                                  style={{ color: '#f97316' }}
                                >
                                  View Profile →
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Duration */}
                      {selectedClass.class_Date_From && selectedClass.class_Date_To && (
                        <div className="bg-purple-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">📅 Duration</p>
                          <p className="font-bold text-gray-900 text-sm leading-tight">{new Date(selectedClass.class_Date_From).toLocaleDateString()} - {new Date(selectedClass.class_Date_To).toLocaleDateString()}</p>
                        </div>
                      )}
                      
                      {/* Sessions */}
                      {selectedClass.class_Number_Of_Sessions && (
                        <div className="bg-indigo-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">📊 Number of Sessions</p>
                          <p className="font-bold text-indigo-600 text-2xl">{selectedClass.class_Number_Of_Sessions}</p>
                        </div>
                      )}
                      
                      {/* Status */}
                      <div className={`rounded-lg p-3 ${selectedClass.class_Capacity > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-sm text-gray-600 mb-1">📋 Status</p>
                        <p className={`font-bold text-base ${selectedClass.class_Capacity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedClass.class_Capacity > 0 ? 'Available' : 'Not Available'}
                        </p>
                      </div>
                      
                      {/* Available Seats */}
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-sm text-gray-600 mb-1">💺 Available Seats</p>
                        <p className="font-bold text-green-600 text-2xl">{selectedClass.class_Capacity || 0}</p>
                      </div>
                      
                      {/* Fee */}
                      {selectedClass.class_Fee && (
                        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">💰 Fee</p>
                          <p className="font-black text-orange-600 text-2xl">$ {selectedClass.class_Fee.toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Description - Full Content with Scroll */}
                    {selectedClass.class_Description && (
                      <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <h3 className="text-base font-bold text-gray-800 mb-2 flex items-center gap-2 sticky top-0 bg-gray-50 pb-2">
                          📝 Class Details
                        </h3>
                        <div 
                          className="text-gray-700 text-base leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: selectedClass.class_Description }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && classes.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No classes available for this center.</p>
          </div>
        )}

        {/* Login Modal */}
        {showLogin && (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onCancel={handleLoginCancel}
            entity="RMS"
          />
        )}

        {/* Sessions Right Drawer */}
        {showSessions && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 z-50 animate-fadeIn"
              onClick={() => setShowSessions(false)}
            ></div>
            
            {/* Right Drawer */}
            <div className="fixed top-0 right-0 h-full w-full md:w-[500px] bg-white shadow-2xl z-50 overflow-y-auto animate-slideInRight">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 shadow-sm">
                <div>
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">📅 Class Sessions</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{selectedClass?.class_Name}</p>
                </div>
                <button 
                  onClick={() => setShowSessions(false)} 
                  className="text-gray-400 hover:text-gray-600 hover:rotate-90 text-xl flex-shrink-0 transition-all duration-300"
                >
                  ✕
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {sessions.length > 0 ? (
                  <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs">Date</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs">Start Time</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs">End Time</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 text-xs">Venue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sessions.map((session, index) => {
                            const sessionDate = session.date ? new Date(session.date) : null;
                            const formattedDate = sessionDate ? `${String(sessionDate.getMonth()+1).padStart(2,'0')}/${String(sessionDate.getDate()).padStart(2,'0')}/${sessionDate.getFullYear()}` : 'TBA';
                            return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-gray-600 text-sm">{formattedDate}</td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{session.startTime || session.start_Time || session.time || 'TBA'}</td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{session.endTime || session.end_Time || 'TBA'}</td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{session.venue || session.comments || 'TBA'}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-gray-200">
                    <p className="text-gray-600">No sessions available for this class.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassSelection;
